/*
  CreateOS.jsx
  ----------------
  Interface pour créer et modifier les Ordres de Service (OS).
*/

import { useEffect, useState, useRef, useMemo } from "react";
import {
  Card,
  Text,
  Center,
  Select,
  NumberInput,
  TextInput,
  Title,
  Textarea,
  Button,
  Box,
  Accordion,
  MultiSelect,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../tools/Context";
import { Dropzone } from "@mantine/dropzone";
import { showNotification } from "@mantine/notifications";
import { get, post, uploadFile } from "../tools/Request.jsx";
import downloadFromBackend from "../tools/DownloadFromBackend.jsx";

const description = {
  "OS de TS": {
    desc: "Notifiant l’exécution de prestations non prévues et, le cas échéant, leurs prix",
    type: `Le formulaire MDP-M-24-1 est un modèle d’ordre de service, qui peut être utilisé pour ordonner des travaux supplémentaires à exécuter, et le cas échéant notifier les nouveaux prix s’y rapportant en application de l'article 14 du CCAG travaux.`,
    descE: `Le titulaire du marché identifié ci-dessus est prié de bien vouloir exécuter les prestations désignées ci-dessous, conformément aux dispositions des documents constitutifs du marché de travaux et aux prescriptions définies ci-dessous, par le présent Ordre de service. Il renvoie au chef de projet de la RATP dûment remplis et signés, l’un des deux exemplaires de cet ordre de service, qui tiendra lieu d’accusé de réception.`,
    descE2: `* Dans le cas où l’OS ordonnant l’exécution des prestations non prévues fixe en même temps les prix nouveaux, les décompositions des prix forfaitaires et les sous-détails de prix unitaires annexés au marché doivent être appliqués pour fixer les nouveaux prix`,
    descE3: `** Total = Prix unitaire X Quantité.`,
    placeHolderPrecision: `(Des descriptions d’ordre technique peuvent être jointes en annexe, dans ce cas les lister au F- ci-dessous ou les préciser dans ce point. Dans l’hypothèse où les nouveaux prix seront notifiés ultérieurement, le préciser également dans ce point)`,
  },
  "OS de prix": {
    desc: "Notifiant le prix de prestations non prévues, ordonnées par un précédent OS",
    type: `Le formulaire MDP-M-24-2 est un modèle d’ordre de service, qui peut être utilisé pour notifier les nouveaux prix de prestations non prévues ordonnées par un précédent OS en application de l'article 14 du CCAG travaux`,
    descE: `Le titulaire du marché identifié ci-dessus est prié de bien vouloir recevoir notification du/des prix nouveaux des prestations non prévues suivantes, qui ont été ordonnées par l’OS n°… du …. :`,
    descE2: `*  Dans le cas où l’OS ordonnant l’exécution des prestations non prévues fixe en même temps les prix nouveaux, les décompositions des prix forfaitaires et les sous-détails de prix unitaires annexés au marché doivent être appliqués pour fixer les nouveaux prix.`,
    descE3: ``,
    placeHolderPrecision: ``,
  },
  "OS de moins-value": {
    desc: "Notifiant une moins-value au marché",
    type: `Le formulaire MDP-M-24-3 est un modèle d’ordre de service, qui peut être utilisé pour notifier le retrait de prestations prévues au marché.`,
    descE: `Le titulaire du marché identifié ci-dessus est informé du retrait de certaines prestations de son marché. Celles-ci sont détaillées ci-après.`,
    descE2: `* : Utiliser les prix du marché pour les prestations retirées.`,
    descE3: `** Total = Prix unitaire X Quantité.`,
    placeHolderPrecision: `(Des descriptions d’ordre technique peuvent être jointes en annexe, dans ce cas les lister au F - ou les préciser dans ce point.)`,
  },
  "OS autre": {
    desc: "",
    type: `Le formulaire MDP-M-24-4 est un modèle d’ordre de service, qui peut être utilisé pour notifier toute disposition à l'exception des celles prévues à l'article 14 du CCAG travaux et des moins values.`,
    descE: "",
    descE2: ``,
    descE3: ``,
    placeHolderPrecision: ``,
  },
};

export default function CreateOS() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const queryParams = new URLSearchParams(location.search);
  const osId = queryParams.get("osId");
  const marketIdFromUrl = queryParams.get("marketId");
  const [opened, setOpened] = useState("");
  const [selectedOS, setSelectedOS] = useState(null);
  const [orders, setOrders] = useState([]);
  const [projectManager, setProjectManager] = useState([]);
  const [fieldManager, setFieldManager] = useState([]);
  const [holder, setHolder] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [linkedOS, setLinkedOS] = useState([]);
  const debounceTimers = useRef({});
  const [osToPrefillList, setOsToPrefillList] = useState([]);

  const originalSnapshotRef = useRef(null);
  const [dirty, setDirty] = useState(false);

  const [formData, setFormData] = useState({
    market_id: "",
    os_type: "",
    os_id: osId || "",
    field_manager_name: user.email || "",
    field_manager_email: user.email || "",
    holder_name: "",
    holder_email: "",
    mandatary: "",
    address: "",
    project_manager: "",
    project_manager_email: "",
    represented_by: "",
    project_manager_role: "signer",
    project_manager_address: "",
    project_name: "",
    batch: "",
    work_market_reference: "",
    ols_number: [],
    notification_date: "",
    execution_time: "",
    execution_address: "",
    execution_delay: "",
    technical_details: "",
    orderedServices: [],
    total_order_amount: 0,
    uploadedFiles: [],
    saved: false,
    linkedos: "",
  });

  const toNumber = (v) => (typeof v === "number" && !isNaN(v) ? v : parseFloat(v) || 0);
  const computeLineTotal = (s) => toNumber(s.quantity) * toNumber(s.unit_price);
  const computeGrandTotal = (services) => services.reduce((sum, s) => sum + computeLineTotal(s), 0);

  useEffect(() => {
    const newTotal = computeGrandTotal(formData.orderedServices);
    if (newTotal !== toNumber(formData.total_order_amount)) {
      setFormData((prev) => ({ ...prev, total_order_amount: newTotal }));
      if (osId && formData.saved === false) {
        post("os/update-os", { osId, field: "total_order_amount", value: newTotal }, user.token).catch(() => {});
      } else {
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.orderedServices]);

  const isFinal = formData.saved === true;

  const snapshotFromData = (data) => ({
    ...data,
    orderedServices: (data.orderedServices || []).map((s) => ({
      id: s.id || "",
      description: s.description || "",
      quantity: toNumber(s.quantity),
      unit_price: toNumber(s.unit_price),
      total_price: toNumber(s.total_price || computeLineTotal(s)),
    })),
  });

  const refreshSnapshot = (data) => {
    originalSnapshotRef.current = snapshotFromData(data);
    setDirty(false);
  };

  useEffect(() => {
    const fetchAllSavedOS = async () => {
      if (formData.market_id) {
        try {
          const response = await get(`os/market/${formData.market_id}/os-names`, null, user.token);
          setOsToPrefillList(response.filter((os) => os.id !== osId));
        } catch (error) {
          console.error("Error fetching OS to prefill:", error);
        }
      }
    };
    fetchAllSavedOS();
  }, [formData.market_id, osId, user.token]);

  useEffect(() => {
    const fetchOrCreateOS = async () => {
      if (!osId) {
        try {
          if (marketIdFromUrl) {
            const response = await get("os", { market_id: marketIdFromUrl }, user.token);
            const newOsId = response.id;
            navigate(`?osId=${newOsId}`, { replace: true });
          } else {
            const response = await get("os", null, user.token);
            const newOsId = response.id;
            navigate(`?osId=${newOsId}`, { replace: true });
          }
        } catch (error) {
          console.error("Error creating a new OS:", error);
        }
      } else {
        try {
          const response = await get(`os/${osId}`, null, user.token);
          const osData = response;
          if (typeof osData.ols_number === "string") {
            try {
              osData.ols_number = JSON.parse(osData.ols_number);
            } catch {
              osData.ols_number = [];
            }
          }
          osData.total_order_amount = toNumber(osData.total_order_amount);
          osData.orderedServices = (osData.orderedServices || []).map((s) => ({
            ...s,
            quantity: toNumber(s.quantity),
            unit_price: toNumber(s.unit_price),
            total_price: toNumber(s.total_price || computeLineTotal(s)),
          }));
          setFormData(osData);
          refreshSnapshot(osData);
        } catch (error) {
          console.error("Error fetching OS data:", error);
        }
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await get("users/getAllUser", null, user.token);

        const projectManagerList = response.filter(
          (u) => u.role === "chef de projet" && u.authorized_markets.includes(formData.market_number)
        );
        const fieldManagerList = response.filter(
          (u) => u.role === "maitre d'oeuvre" && u.authorized_markets.includes(formData.market_number)
        );
        const holderList = response.filter(
          (u) => u.role === "titulaire de marché" && u.authorized_markets.includes(formData.market_number)
        );

        setProjectManager(projectManagerList);
        setFieldManager(fieldManagerList);
        setHolder(holderList);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchMarkets = async () => {
      try {
        const response = await get("market", null, user.token);
        setMarkets(response);
      } catch (error) {
        console.error("Error fetching markets:", error);
      }
    };

    fetchOrCreateOS();
    fetchUsers();
    fetchMarkets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osId, formData.market_number, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (formData.market_id) {
        try {
          const response = await get(`order/market/${formData.market_id}`, null, user.token);
          setOrders(response);
        } catch (error) {
          console.error("Error fetching orders:", error);
        }
      }
    };
    fetchOrders();
  }, [formData.market_id, user.token]);

  useEffect(() => {
    const fetchLinkedOS = async () => {
      if (formData.market_id) {
        try {
          const response = await get(`os/market/${formData.market_id}/os-names`, null, user.token);
          setLinkedOS(response);
        } catch (error) {
          console.error("Error fetching linked OS:", error);
        }
      }
    };
    fetchLinkedOS();
  }, [formData.market_id, user.token]);

  const persistFieldIfDraft = async (field, value) => {
    if (osId && formData.saved === false) {
      await post("os/update-os", { osId, field, value }, user.token);
    } else {
      setDirty(true);
    }
  };

  const handleChange = async (field, value) => {
    try {
      if (field === "market_id") {
        const selectedMarket = markets.find((m) => m.id === value);
        const marketNumber = selectedMarket?.market_number || "";

        setFormData((prevFormData) => {
          const newFormData = {
            ...prevFormData,
            market_id: value,
            market_number: marketNumber,
            work_market_reference: marketNumber,
          };
          if (!prevFormData.field_manager_email) {
            newFormData.field_manager_email = user.email;
          }
          return newFormData;
        });

        if (formData.saved === false) {
          await post("os/update-os", { osId, field: "market_id", value }, user.token);
          await post("os/update-os", { osId, field: "work_market_reference", value: marketNumber }, user.token);
          if (!formData.field_manager_email) {
            await post("os/update-os", { osId, field: "field_manager_email", value: user.email }, user.token);
          }
        } else {
          setDirty(true);
        }

        const response = await get("users/getAllUser", null, user.token);

        const projectManagerList = response.filter(
          (u) => u.role === "chef de projet" && u.authorized_markets.includes(marketNumber)
        );
        const fieldManagerList = response.filter(
          (u) => u.role === "maitre d'oeuvre" && u.authorized_markets.includes(marketNumber)
        );
        const holderList = response.filter(
          (u) => u.role === "titulaire de marché" && u.authorized_markets.includes(marketNumber)
        );

        setProjectManager(projectManagerList);
        setFieldManager(fieldManagerList);
        setHolder(holderList);

        const updates = [];
        const cleared = {};
        if (!projectManagerList.some((u) => u.email === formData.project_manager_email)) {
          cleared.project_manager_email = "";
          if (formData.saved === false)
            updates.push(post("os/update-os", { osId, field: "project_manager_email", value: "" }, user.token));
        }
        if (!holderList.some((u) => u.email === formData.holder_email)) {
          cleared.holder_email = "";
          if (formData.saved === false)
            updates.push(post("os/update-os", { osId, field: "holder_email", value: "" }, user.token));
        }
        if (Object.keys(cleared).length) setFormData((prev) => ({ ...prev, ...cleared }));
        if (formData.saved === false && updates.length) await Promise.allSettled(updates);
        return;
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        [field]: value,
      }));

      if (field !== "saved") {
        await persistFieldIfDraft(field, value);
      }

      if (field === "saved") {
        const res = await post("os/update-os", { osId, field, value }, user.token);
        if (value === true && res?.osName) {
          showNotification({
            title: "Succès",
            message: "Ordre de service enregistré avec succès.",
            color: "green",
          });
          const fresh = await get(`os/${osId}`, null, user.token);
          setFormData(fresh);
          refreshSnapshot(fresh);
          navigate(`/myos?osName=${res.osName}`, { replace: true });
        }
      }

      if (field === "os_type") {
        setSelectedOS(description[value]);
      } else {
        if (isFinal) setDirty(true);
      }
    } catch (error) {
      console.error("Error updating OS:", error);
      showNotification({
        title: "Une erreur est survenue",
        message: error.response?.data?.error || "Erreur inattendue. Veuillez réessayer.",
        color: "red",
      });
    }
  };

  const handleRemoveAttestation = async (index, service) => {
    try {
      if (debounceTimers.current[index]) {
        clearTimeout(debounceTimers.current[index]);
        delete debounceTimers.current[index];
      }

      setFormData((prev) => {
        const updated = [...prev.orderedServices];
        updated.splice(index, 1);
        const newTotal = computeGrandTotal(updated);
        return { ...prev, orderedServices: updated, total_order_amount: newTotal };
      });

      if (formData.saved === false) {
        if (service?.id) {
          await post(`os/remove-ordered-service`, { osId, serviceId: service.id }, user.token);
        }
        const newTotal = computeGrandTotal((formData.orderedServices || []).filter((_, i) => i !== index));
        await post("os/update-os", { osId, field: "total_order_amount", value: newTotal }, user.token);
      } else {
        setDirty(true);
      }

      if (opened === `service-${index}`) setOpened("");
    } catch (error) {
      console.error("Error removing ordered service:", error);
      showNotification({
        title: "Suppression impossible",
        message: "La prestation n'a pas pu être supprimée.",
        color: "red",
      });
    }
  };

  const handleAddOrderedService = () => {
    setFormData((prev) => ({
      ...prev,
      orderedServices: [
        ...prev.orderedServices,
        { id: "", description: "", quantity: 0, unit_price: 0, total_price: 0 },
      ],
    }));
    if (isFinal) setDirty(true);
  };

  const handleOrderedServiceChange = (index, field, rawValue) => {
    const isNumeric = field === "quantity" || field === "unit_price";
    const value = isNumeric ? toNumber(rawValue) : rawValue;

    const updatedServices = formData.orderedServices.map((service, i) => {
      if (i !== index) return service;
      const next = { ...service, [field]: value };
      if (field === "quantity" || field === "unit_price") {
        next.total_price = computeLineTotal(next);
      }
      return next;
    });

    const newGrandTotal = computeGrandTotal(updatedServices);
    setFormData({ ...formData, orderedServices: updatedServices, total_order_amount: newGrandTotal });

    if (formData.saved === false) {
      clearTimeout(debounceTimers.current[index]);
      debounceTimers.current[index] = setTimeout(async () => {
        const service = updatedServices[index];
        try {
          const resp = await post(
            "os/update-ordered-service",
            { osId, serviceId: service.id, field, value },
            user.token
          );

          let currentServiceId = service.id;
          if (!service.id && resp?.newServiceId) {
            currentServiceId = resp.newServiceId;
            setFormData((prev) => {
              const arr = [...prev.orderedServices];
              arr[index] = { ...arr[index], id: currentServiceId };
              return { ...prev, orderedServices: arr };
            });
          }

          if (field === "quantity" || field === "unit_price") {
            await post(
              "os/update-ordered-service",
              {
                osId,
                serviceId: currentServiceId || service.id,
                field: "total_price",
                value: computeLineTotal(service),
              },
              user.token
            );
          }

          const latestGrand = computeGrandTotal(updatedServices);
          await post("os/update-os", { osId, field: "total_order_amount", value: latestGrand }, user.token);
        } catch (error) {
          console.error("Error updating ordered service:", error);
        }
      }, 500);
    } else {
      setDirty(true);
    }
  };

  const handleFileDrop = async (files) => {
    const formDataUpload = new FormData();
    files.forEach((file) => {
      formDataUpload.append("files", file);
    });
    formDataUpload.append("osId", osId);
    try {
      const response = await uploadFile("file/add-document", formDataUpload, user.token);
      setFormData((prevFormData) => ({
        ...prevFormData,
        uploadedFiles: [...prevFormData.uploadedFiles, ...response.files],
      }));
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleRemoveFile = async (fileId) => {
    try {
      await post(`os/remove-document`, { documentId: fileId }, user.token);
      setFormData((prevFormData) => ({
        ...prevFormData,
        uploadedFiles: prevFormData.uploadedFiles.filter((file) => file.id !== fileId),
      }));
    } catch (error) {
      console.error("Error removing file:", error);
    }
  };

  const pmOptions = projectManager.map((u) => ({ value: u.email, label: u.email }));
  const holderOptions = holder.map((u) => ({ value: u.email, label: u.email }));

  const pmDisabled = !formData.market_id || pmOptions.length === 0;
  const holderDisabled = !formData.market_id || holderOptions.length === 0;

  const pmPlaceholder = !formData.market_id
    ? "Sélectionnez un marché d'abord"
    : pmDisabled
    ? "Aucun chef de projet disponible pour ce marché"
    : "Recherchez un chef de projet";

  const holderPlaceholder = !formData.market_id
    ? "Sélectionnez un marché d'abord"
    : holderDisabled
    ? "Aucun titulaire de marché disponible pour ce marché"
    : "Recherchez un titulaire de marché";

  const requiredFields = ["market_id", "os_type", "project_manager_email", "holder_email", "field_manager_email"];
  const isFormValid = requiredFields.every((field) => {
    const value = formData[field];
    return value !== "" && value !== null && value !== undefined;
  });

  // >>> UPDATED: redirect after saving changes
  const handleCommitChanges = async () => {
    try {
      const baseline = originalSnapshotRef.current || snapshotFromData(formData);
      const current = snapshotFromData(formData);

      const fieldsToCheck = [
        "market_id",
        "os_type",
        "field_manager_email",
        "holder_email",
        "mandatary",
        "address",
        "project_manager_email",
        "project_manager_role",
        "project_manager_address",
        "project_name",
        "batch",
        "work_market_reference",
        "notification_date",
        "execution_time",
        "execution_address",
        "execution_delay",
        "technical_details",
        "total_order_amount",
        "linkedos",
        "ols_number",
      ];

      const fieldUpdates = [];
      for (const f of fieldsToCheck) {
        const was = baseline[f];
        const now = current[f];
        const changed =
          Array.isArray(now) || Array.isArray(was) ? JSON.stringify(now) !== JSON.stringify(was) : now !== was;

        if (changed) {
          fieldUpdates.push(
            post("os/update-os", { osId, field: f, value: now }, user.token).catch((e) => {
              console.error("Field update failed", f, e);
            })
          );
        }
      }

      const baselineMap = new Map((baseline.orderedServices || []).map((s) => [String(s.id || ""), s]));
      const currentMap = new Map((current.orderedServices || []).map((s) => [String(s.id || ""), s]));

      const deleted = [...baselineMap.keys()].filter((id) => id && !currentMap.has(id));
      const createOrUpdatePromises = [];

      for (const delId of deleted) {
        if (delId) {
          createOrUpdatePromises.push(
            post("os/remove-ordered-service", { osId, serviceId: delId }, user.token).catch((e) =>
              console.error("Remove service failed", e)
            )
          );
        }
      }

      for (const service of current.orderedServices || []) {
        if (service.id) {
          const prev = baselineMap.get(String(service.id)) || {};
          const serviceFields = ["description", "quantity", "unit_price", "total_price"];
          for (const sf of serviceFields) {
            if (toNumber(prev[sf]) !== toNumber(service[sf]) || (prev[sf] || "") !== (service[sf] || "")) {
              createOrUpdatePromises.push(
                post(
                  "os/update-ordered-service",
                  { osId, serviceId: service.id, field: sf, value: service[sf] },
                  user.token
                ).catch((e) => console.error("Update service failed", e))
              );
            }
          }
        } else {
          for (const sf of ["description", "quantity", "unit_price", "total_price"]) {
            createOrUpdatePromises.push(
              post("os/update-ordered-service", { osId, serviceId: "", field: sf, value: service[sf] }, user.token)
            );
          }
        }
      }

      await Promise.all([...fieldUpdates, ...createOrUpdatePromises]);

      const fresh = await get(`os/${osId}`, null, user.token);
      setFormData(fresh);
      refreshSnapshot(fresh);

      showNotification({
        title: "Modifications enregistrées",
        message: "Toutes les modifications ont été sauvegardées.",
        color: "green",
      });

      if (fresh?.os_name) {
        navigate(`/myos?osName=${fresh.os_name}`, { replace: true });
      }
    } catch (e) {
      console.error(e);
      showNotification({
        title: "Erreur",
        message: "Impossible d’enregistrer les modifications.",
        color: "red",
      });
    }
  };

  const primarySaveButton = useMemo(() => {
    if (!formData.market_id) return null;

    if (formData.saved === false) {
      return (
        <Button color="#0a0082" mt={20} fullWidth onClick={() => handleChange("saved", true)} disabled={!isFormValid}>
          {!isFormValid ? "Veuillez renseigner tous les champs marqués d'une étoile rouge" : "Enregistrer"}
        </Button>
      );
    }

    if (formData.saved === true && dirty) {
      return (
        <Button color="#0a0082" mt={20} fullWidth onClick={handleCommitChanges}>
          Enregistrer les modifications
        </Button>
      );
    }

    return null;
  }, [formData.saved, formData.market_id, isFormValid, dirty]); // eslint-disable-line

  return (
    <Center>
      <Card shadow="sm" padding="lg" radius="md" withBorder w="80%" mt={40}>
        <Title>Création d'OS</Title>
        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Select
            label="Numéro de marché"
            data={markets.map((market) => ({
              value: market.id,
              label: market.market_number,
            }))}
            withAsterisk
            value={formData.market_id}
            searchable
            onChange={(value) => handleChange("market_id", value)}
          />
          <Select
            label="Remplir à partir d'un OS existant"
            searchable
            placeholder={formData.market_id ? "Choisissez un OS existant" : "Sélectionnez un marché d'abord"}
            data={osToPrefillList.map((os) => ({
              value: os.id,
              label: os.os_name || `OS ${os.id}`,
            }))}
            onChange={async (selectedOsId) => {
              try {
                const selectedOSData = await get(`os/${selectedOsId}`, null, user.token);
                if (typeof selectedOSData.ols_number === "string") {
                  try {
                    selectedOSData.ols_number = JSON.parse(selectedOSData.ols_number);
                  } catch {
                    selectedOSData.ols_number = [];
                  }
                }
                selectedOSData.orderedServices = (selectedOSData.orderedServices || []).map((s) => ({
                  ...s,
                  quantity: toNumber(s.quantity),
                  unit_price: toNumber(s.unit_price),
                  total_price: toNumber(s.total_price || computeLineTotal(s)),
                }));
                selectedOSData.total_order_amount = computeGrandTotal(selectedOSData.orderedServices);

                setFormData((prev) => ({
                  ...prev,
                  ...selectedOSData,
                  os_id: prev.os_id,
                  os_name: "",
                  uploadedFiles: [],
                  saved: prev.saved,
                }));
                if (isFinal) setDirty(true);
                showNotification({
                  title: "Champs remplis",
                  message: "Les champs ont été remplis à partir de l'OS sélectionné.",
                  color: "green",
                });
              } catch (error) {
                console.error("Error loading OS data:", error);
                showNotification({
                  title: "Erreur",
                  message: "Impossible de charger les données de l'OS.",
                  color: "red",
                });
              }
            }}
          />
          <Select
            withAsterisk
            label="Sélectionner le type d'OS"
            data={["OS de TS", "OS de prix", "OS de moins-value", "OS autre"]}
            value={formData.os_type}
            onChange={(value) => handleChange("os_type", value)}
          />
          {selectedOS && (
            <Box mt="md">
              <Text mb={20}>{selectedOS.desc}</Text>
              <Text>{selectedOS.type}</Text>
            </Box>
          )}
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>A - Identification du Maître d’Ouvrage.</Text>
          <Text c="dimmed">
            Régie Autonome des Transports Parisiens, désignée par les initiales « RATP » et représentée par :
          </Text>

          <Select
            mt={5}
            mb={5}
            searchable
            withAsterisk
            label="Chef de projet"
            data={pmOptions}
            value={formData.project_manager_email}
            onChange={(value) => handleChange("project_manager_email", value)}
            placeholder={pmPlaceholder}
            disabled={pmDisabled}
          />
          {formData.market_id && pmOptions.length === 0 && (
            <Text size="sm" c="dimmed" mt={-5} mb={5}>
              Aucun chef de projet n’est rattaché à ce numéro de marché. Ajoutez un utilisateur avec le rôle « chef de
              projet » autorisé pour {formData.market_number || "ce marché"} dans l’administration.
            </Text>
          )}

          <Select
            label="Rôle du chef de projet"
            withAsterisk
            data={[
              { value: "signer", label: "Signataire" },
              { value: "viewer", label: "Lecteur" },
            ]}
            value={formData.project_manager_role}
            onChange={(value) => handleChange("project_manager_role", value)}
            placeholder="Sélectionnez un rôle pour le chef de projet"
            disabled={!formData.project_manager_email}
          />

          <Text c="dimmed">Adresse : 11, avenue Loison Bobet LAC VP.. – 94120 Fontenay-sous-Bois Cedex</Text>
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>B - Identification du titulaire du marché de travaux.</Text>
          <Select
            mt={5}
            mb={5}
            searchable
            withAsterisk
            label="Titulaire du marché"
            data={holderOptions}
            value={formData.holder_email}
            onChange={(value) => handleChange("holder_email", value)}
            placeholder={holderPlaceholder}
            disabled={holderDisabled}
          />
          {formData.market_id && holderOptions.length === 0 && (
            <Text size="sm" c="dimmed" mt={-5} mb={5}>
              Aucun titulaire n’est rattaché à ce numéro de marché. Ajoutez un utilisateur avec le rôle « titulaire de
              marché » autorisé pour {formData.market_number || "ce marché"} dans l’administration.
            </Text>
          )}

          <Text>Indication du mandataire en cas de groupement</Text>
          <TextInput
            label="Représenté par"
            value={formData.mandatary}
            onChange={(event) => handleChange("mandatary", event.target.value)}
          />
          <TextInput
            label="Adresse"
            value={formData.address}
            onChange={(event) => handleChange("address", event.target.value)}
          />
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>C - Identification du maître d’œuvre.</Text>
          <Select
            mt={5}
            mb={5}
            withAsterisk
            searchable
            label="Maître d'oeuvre"
            data={fieldManager.map((u) => ({
              value: u.email,
              label: u.email,
            }))}
            value={formData.field_manager_email}
            onChange={(value) => handleChange("field_manager_email", value)}
            disabled={true}
          />

          <TextInput
            label="Représenté par"
            value={formData.represented_by}
            onChange={(event) => handleChange("represented_by", event.target.value)}
          />
          <TextInput
            label="Adresse"
            value={formData.project_manager_address}
            onChange={(event) => handleChange("project_manager_address", event.target.value)}
          />
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>D - Objet du marché de travaux.</Text>
          <TextInput
            label="Projet"
            placeholder="désignation projet"
            value={formData.project_name}
            onChange={(event) => handleChange("project_name", event.target.value)}
          />
          <TextInput
            label="Lot"
            placeholder="désignation lot"
            value={formData.batch}
            onChange={(event) => handleChange("batch", event.target.value)}
          />
          <TextInput
            label="Référence du marché de travaux"
            placeholder="DEPAAxxxx"
            value={formData.work_market_reference}
            onChange={(event) => handleChange("work_market_reference", event.target.value)}
          />
          <MultiSelect
            label="N° d'OLS"
            placeholder={formData.market_id ? "Sélectionnez un OLS" : "Sélectionnez un marché d'abord"}
            data={
              orders.length > 0 &&
              orders.map((order) => ({
                value: order.order_id,
                label: order.order_number,
              }))
            }
            value={formData.ols_number || []}
            onChange={(value) => handleChange("ols_number", value)}
            disabled={!formData.market_id || orders.length === 0}
          />

          <TextInput
            label="Date de la notification du marché de travaux"
            value={formData.notification_date}
            onChange={(event) => handleChange("notification_date", event.target.value)}
            placeholder="DD/MM/YYYY"
          />
          <NumberInput
            label="Durée d'exécution du marché de travaux (mois)"
            value={formData.execution_time}
            onChange={(value) => handleChange("execution_time", value)}
          />
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>E - Prestations ordonnées et, le cas échéant, leurs prix.</Text>
          {selectedOS && (
            <Box mt="md">
              <Text mb={20}>{selectedOS.descE}</Text>
            </Box>
          )}
          {selectedOS && formData.os_type === "OS de TS" && (
            <>
              <TextInput
                label="Adresse d'exécution des prestations ordonnées"
                value={formData.execution_address}
                onChange={(event) => handleChange("execution_address", event.target.value)}
              />
              <TextInput
                label="Délai d'exécution des prestations ordonnées"
                value={formData.execution_delay}
                onChange={(event) => handleChange("execution_delay", event.target.value)}
              />
            </>
          )}
          {selectedOS && (formData.os_type === "OS de TS" || formData.os_type === "OS de moins-value") && (
            <Textarea
              label="Autres précisions"
              placeholder="(Des descriptions d’ordre technique peuvent être jointes en annexe, dans ce cas les lister au F- ci-dessous ou les préciser dans ce point. Dans l’hypothèse où les nouveaux prix seront notifiés ultérieurement, le préciser également dans ce point)"
              value={formData.technical_details}
              onChange={(event) => handleChange("technical_details", event.target.value)}
            />
          )}

          <Box p="md" mt="md" style={{ border: "1px solid #eaeaea", borderRadius: "8px" }}>
            <Accordion variant="contained" mt="md" value={opened} onChange={setOpened}>
              {formData.orderedServices.map((service, index) => (
                <Accordion.Item value={`service-${index}`} key={index}>
                  <Accordion.Control>
                    <Text style={{ fontWeight: 500 }}>{`Prestation ordonnée ${index + 1}`}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <TextInput
                      label="Désignation des prestations ordonnées"
                      value={service.description}
                      onChange={(event) => handleOrderedServiceChange(index, "description", event.target.value)}
                    />
                    <NumberInput
                      label="Quantité"
                      value={service.quantity}
                      onChange={(value) => handleOrderedServiceChange(index, "quantity", value)}
                      min={0}
                    />
                    <NumberInput
                      label="Prix unitaire HT"
                      value={service.unit_price}
                      onChange={(value) => handleOrderedServiceChange(index, "unit_price", value)}
                      min={0}
                      precision={2}
                    />
                    <NumberInput
                      label="Total HT (calculé)"
                      value={toNumber(service.total_price)}
                      disabled
                      readOnly
                      precision={2}
                    />
                    <Box mt="md" style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button variant="light" color="#e2231a" onClick={() => handleRemoveAttestation(index, service)}>
                        Supprimer la prestation
                      </Button>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
            <Center>
              <Button color="#0a0082" onClick={handleAddOrderedService} mt={10}>
                Ajouter une prestation ordonnée
              </Button>
            </Center>
          </Box>

          <NumberInput
            label="Montant total de l’ordre de service (calculé)"
            value={toNumber(formData.total_order_amount)}
            disabled
            readOnly
            precision={2}
          />

          {selectedOS && (
            <>
              <Box mt="md">
                <Text mb={20}>{selectedOS.descE2}</Text>
              </Box>
              <Box mt="md">
                <Text mb={20}>{selectedOS.descE3}</Text>
              </Box>
            </>
          )}
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>F – Pièces jointes</Text>

          {formData.uploadedFiles.length > 0 && (
            <Accordion variant="contained" mt="md" value={opened} onChange={setOpened}>
              {formData.uploadedFiles.map((file, index) => (
                <Accordion.Item value={`file-${index}`} key={index}>
                  <Accordion.Control>
                    <Text style={{ fontWeight: 500 }}>{file.name}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Box mt="md" style={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button variant="light" color="blue" mr={20} onClick={() => downloadFromBackend(file.id)}>
                        Télécharger le fichier
                      </Button>
                      <Button variant="light" color="#e2231a" onClick={() => handleRemoveFile(file.id)}>
                        Supprimer le fichier
                      </Button>
                    </Box>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
          <Dropzone
            mt={20}
            onDrop={handleFileDrop}
            onReject={(files) => console.log("Rejected files:", files)}
            maxSize={5 * 1024 ** 2}
          >
            <Center>
              <Text size="md" c="dimmed">
                Ajoutez vos pièces jointes ici
              </Text>
            </Center>
          </Dropzone>
        </Box>

        <Box p="md" mt="md" style={{ border: "1.5px solid #eaeaea", borderRadius: "8px" }}>
          <Text>Lié à un OS</Text>
          <Select
            mt={5}
            mb={5}
            searchable
            label="Lié à un OS"
            data={linkedOS
              .filter((os) => os.id !== osId)
              .map((os) => ({
                value: os.id,
                label: os.os_name || `OS ${os.id}`,
              }))}
            value={formData.linkedos}
            onChange={(value) => handleChange("linkedos", value)}
            placeholder="Sélectionnez un OS lié"
          />
        </Box>

        {primarySaveButton}
      </Card>
    </Center>
  );
}
